const list = value => Array.isArray(value) ? value : typeof value === 'string' ? value.split(';').filter(Boolean) : [];
const parse = value => { try { return typeof value === 'string' ? JSON.parse(value) : value || []; } catch { return []; } };
const label = entity => entity.display_name || entity.name;
const endpoint = value => typeof value === 'object' ? value.id : value;
const add = (map, key, value) => { if (key && value) map.set(key, [...new Set([...(map.get(key) || []), value])]); };

export function studyCohortIndex(provenance) {
  const entities = provenance?.entities || [];
  const byId = new Map(entities.map(entity => [entity.entity_id, entity]));
  const byTitle = new Map(entities.map(entity => [entity.graph_title, entity]));
  if (provenance?.source_manifest) {
    const sourceCohorts = new Map(); const sourceStudies = new Map();
    const studiesForCohort = new Map(); const cohortsForStudy = new Map();
    for (const document of provenance.source_manifest.documents || []) {
      sourceCohorts.set(document.source_file, (document.current_cohort_ids || []).map(id => label(byId.get(id) || {})).filter(Boolean));
      sourceStudies.set(document.source_file, (document.current_study_ids || []).map(id => label(byId.get(id) || {})).filter(Boolean));
    }
    for (const entity of entities) {
      (entity.studies || []).forEach(name => add(studiesForCohort, entity.entity_id, name));
      (entity.cohorts || []).forEach(name => add(cohortsForStudy, entity.entity_id, name));
    }
    const cohortNames = new Map(entities.filter(e => e.concept_type === 'Cohort').flatMap(e => [e.name, ...parse(e.aliases)].filter(Boolean).map(name => [name.toLowerCase(), e])));
    // The workflow already exports grounded membership edges. No UI inference.
    return { byTitle, cohortNames, sourceStudies, sourceCohorts, studiesForCohort, cohortsForStudy, associations: [] };
  }
  const cohorts = entities.filter(entity => entity.concept_type === 'Cohort');
  const cohortNames = new Map(cohorts.flatMap(entity => [entity.name, entity.display_name, ...parse(entity.aliases)]
    .filter(Boolean).map(name => [name.toLowerCase(), entity])));
  const sourceCohorts = new Map();
  const sourceStudies = new Map();
  const associations = new Map();
  const mentions = (entity, source) => parse(entity.source_mentions).filter(mention => mention.source === source);
  const relate = (study, cohort, source, method) => {
    if (!study || !cohort || study.concept_type === 'Cohort' || cohort.concept_type !== 'Cohort') return;
    add(sourceStudies, source, label(study));
    const key = JSON.stringify([study.entity_id, cohort.entity_id, source]);
    if (associations.has(key)) return;
    const evidence = [...mentions(study, source), ...mentions(cohort, source)].map(mention => mention.evidence).filter(Boolean).join('\n');
    associations.set(key, { study, cohort, record: {
      source: label(study), target: label(cohort), source_file: source,
      relationship_kind: 'study_cohort', predicate: 'uses_cohort', assertion: 'descriptive',
      description: `${label(study)} uses data from ${label(cohort)}.`,
      mapping_method: method, needs_review: method === 'current_study_source_context',
      evidence, statistics: [], adjustments: [],
    } });
  };
  cohorts.filter(entity => !entity.study_scope && !/background|author_or_reference/.test(entity.source_roles || ''))
    .forEach(entity => list(entity.sources).forEach(source => add(sourceCohorts, source, entity.entity_id)));
  for (const entity of entities) {
    for (const occurrence of parse(entity.population_occurrences)) {
      const cohort = byId.get(occurrence.parent_cohort_id)
        || (byId.get(occurrence.study_id)?.concept_type === 'Cohort' ? byId.get(occurrence.study_id) : null)
        || cohortNames.get((occurrence.parent_cohort || occurrence.study || '').toLowerCase());
      if (!cohort) continue;
      add(sourceCohorts, occurrence.source_file, cohort.entity_id);
      relate(byId.get(occurrence.study_id), cohort, occurrence.source_file, 'explicit_population_occurrence');
    }
  }
  // Current analyses sharing an explicitly recorded cohort's source context.
  // Cited/background/planned studies do not acquire cohort membership this way.
  entities.filter(entity => entity.concept_type === 'Study' && !entity.study_scope
    && !/background|author_or_reference/.test(entity.source_roles || '')).forEach(study => {
    list(study.sources).forEach(source => {
      add(sourceStudies, source, label(study));
      (sourceCohorts.get(source) || []).forEach(id => relate(study, byId.get(id), source, 'current_study_source_context'));
    });
  });
  const studiesForCohort = new Map();
  const cohortsForStudy = new Map();
  for (const association of associations.values()) {
    add(studiesForCohort, association.cohort.entity_id, label(association.study));
    add(cohortsForStudy, association.study.entity_id, label(association.cohort));
  }
  return { byTitle, cohortNames, sourceStudies, sourceCohorts: new Map([...sourceCohorts].map(([source, ids]) => [source, ids.map(id => label(byId.get(id)))])),
    studiesForCohort, cohortsForStudy, associations: [...associations.values()] };
}

export function splitStudyCohortGraph(data, provenance) {
  const index = studyCohortIndex(provenance);
  const nodes = data.nodes.map(node => {
    const entity = index.byTitle.get(node.id);
    const category = (entity?.concept_type || node.concept_type) === 'Cohort' ? 'Cohort' : 'Study';
    return { ...node, type: node.type === 'Study / Cohort' ? category : node.type,
      category_tags: (node.category_tags || [node.type]).map(tag => tag === 'Study / Cohort' ? category : tag) };
  });
  const ids = new Set(nodes.map(node => node.id));
  const existing = new Set(data.links.map(link => JSON.stringify([endpoint(link.source), endpoint(link.target)].sort())));
  const added = new Map();
  for (const { study, cohort, record } of index.associations) {
    if (!ids.has(study.graph_title) || !ids.has(cohort.graph_title)) continue;
    const pair = JSON.stringify([study.graph_title, cohort.graph_title].sort());
    if (existing.has(pair)) continue;
    if (!added.has(pair)) added.set(pair, { id: `study-cohort:${study.entity_id}:${cohort.entity_id}`, source: study.graph_title,
      target: cohort.graph_title, type: 'uses_cohort', displayRecords: [], weight: 0, text_unit_ids: [] });
    const edge = added.get(pair);
    edge.displayRecords.push(record);
    edge.weight++;
    edge.description = edge.displayRecords.map(value => JSON.stringify(value)).join('\n');
  }
  return { nodes, links: [...data.links, ...added.values()] };
}
